import { AssetSvg } from '@/components/foundation/Svg'
import { Translation } from '@/components/foundation/Translation'
import { useDataConfigSearch } from '@/hooks/data/useDataConfigSearch'
import { useClickOut } from '@/hooks/useClickOut'
import { useMobile } from '@/hooks/useMobile'
import { useModule } from '@/hooks/useModule'
import { useNewAutocomplete } from '@/hooks/useNewAutocomplete'
import { useTranslator } from '@/hooks/useTranslator'
import { modeTheme } from '@/theme'
import type { AutocompleteSearch } from '@/types/Autocomplete'
import { apiCall } from '@/util/apiCall'
import {
	getPosition,
	getSuggestion,
	getSuggestionLabel,
	matchSearchRule,
} from '@/util/autocomplete'
import type { ModuleObject } from '@/util/modules'
import type { Address } from '@common/types/Address'
import type { AutocompleteResult } from '@common/types/AutocompleteResult'
import type { ConfigSearch } from '@common/types/config/Search'
import type { Poi } from '@common/types/Poi'
import { classes, mergeRefs, type Children } from '@lc/libreact'
import { safeProp } from '@lc/libutils'
import {
	useCallback,
	useMemo,
	useRef,
	useState,
	type ComponentProps,
	type ComponentType,
	type ForwardedRef,
} from 'react'
import { Button } from '../Button'
import { LinePicto } from '../LinePicto'
import { Modal } from '../Modal'
import styles from './NewAutocompleteInput.scss'

type IconByResultType = {
	address: string
	poi: string
	boundary: string
	area: string
	line: string
}

const iconByResult: IconByResultType = {
	address: 'assets/Location.svg',
	poi: 'assets/ServicePoint.svg',
	boundary: 'assets/Boundary.svg',
	area: 'assets/ModeBus.svg',
	line: 'assets/ModeBus.svg',
}

export type IntrinsicAutocompleteInputProps = {
	onSelect: (result: AutocompleteResult & { def: string }) => void
	onChange?: (search: string) => void
	onFocus?: () => void
	onBlur?: () => void
	keepFocus?: boolean
	ref?: ForwardedRef<HTMLInputElement>
	placeholder?: string
	noGlass?: boolean
	resetButtonClass?: string
	inputClass?: string
	glassClass?: string
	isSearchBar?: boolean
	noGeoloc?: boolean
}

function renderAutocompleteIcon(
	result: AutocompleteResult & {
		def: string
	},
) {
	return (
		<AssetSvg
			className={styles.icon}
			name={iconByResult[result.type as keyof IconByResultType]}
		/>
	)
}

export function NewControlledAutocompleteInput({
	search,
	results: fullResults,
	setSearch,
	onSelect,
	onChange,
	onFocus,
	onBlur,
	keepFocus = false,
	placeholder,
	noGlass,
	ref,
	resetButtonClass,
	inputClass,
	glassClass,
	isSearchBar = false,
	noGeoloc = false,
}: ReturnType<typeof useNewAutocomplete> & IntrinsicAutocompleteInputProps) {
	const translator = useTranslator()
	const currentModule = useModule()
	const [focused, setFocused] = useState(keepFocus)
	const [focusedIndex, setFocusedIndex] = useState(0)
	const { configSearch } = useDataConfigSearch({
		level: 0,
		disable: !search,
	})
	const results = useMemo(
		() =>
			configSearch
				? filterResults(fullResults, configSearch, currentModule)
				: [],
		[fullResults, configSearch, currentModule],
	)
	const isMobile = useMobile()

	const inputRef = useRef<HTMLInputElement>(null)
	const ulRef = useRef<HTMLUListElement>(null)
	useClickOut(inputRef, ulRef, () => {
		if (keepFocus) return
		setFocused(false)
	})

	const refs = useMemo(() => mergeRefs(inputRef, ref), [inputRef, ref])

	const handleKeyDownOnInput = useCallback(
		(event: React.KeyboardEvent<HTMLInputElement>) => {
			if (results.length === 0) {
				return
			}

			switch (event.key) {
				case 'ArrowDown': {
					event.preventDefault()
					setFocusedIndex(prev =>
						prev + 1 >= results.length ? 0 : prev + 1,
					)
					break
				}
				case 'ArrowUp': {
					event.preventDefault()
					setFocusedIndex(prev =>
						prev - 1 < 0 ? results.length - 1 : prev - 1,
					)
					break
				}
				case 'Escape': {
					setFocusedIndex(0)
					setFocused(false)
					break
				}
				case 'Enter': {
					event.preventDefault()
					if (focusedIndex >= 0 && focusedIndex < results.length) {
						handleResultClick(results[safeProp(focusedIndex)])
					}
					break
				}
				case 'Tab': {
					setFocusedIndex(0)
					setFocused(false)
					break
				}
				default:
					break
			}
		},
		[results, focusedIndex],
	)

	const handleResultClick = (
		result: AutocompleteResult & { def: string },
	) => {
		setFocused(false)
		setFocusedIndex(0)
		setSearch(getSuggestionLabel(result))
		onSelect(result)
		onBlur?.()
	}

	const handleGeolocalisation = async () => {
		try {
			if (!navigator.geolocation) {
				throw new Error('Geolocation is not supported by this browser.')
			}

			const position = await getPosition()

			const result = await apiCall<{
				type: 'address'
				address: Address
			}>('/reverse-geocoding', {
				method: 'GET',
				params: {
					query: `[${position.coords.latitude},${position.coords.longitude}]`,
				},
			})

			if (!result)
				throw new Error('Failed to get address from geolocation.')

			setSearch(result.address.long_name)
			onSelect(result as AutocompleteResult & { def: string })
		} catch (err) {
			console.error(err)
			return []
		}
	}
	return (
		<div className={styles.container}>
			<div className={styles.inputWrapper} ref={inputRef}>
				{!noGlass && (
					<AssetSvg
						className={classes(styles.iconGlassSearch, glassClass)}
						name={'assets/MagnifyingGlass.svg'}
					/>
				)}
				<input
					className={classes(
						styles.input,
						[styles.noGlass, noGlass],
						inputClass,
					)}
					type="search"
					value={search}
					placeholder={placeholder}
					onChange={e => {
						setSearch(e.target.value)
						onChange?.(e.target.value)
						setFocusedIndex(0)
					}}
					onFocus={() => {
						if (keepFocus) return
						onFocus?.()
						setFocused(true)
					}}
					ref={refs}
					onKeyDown={handleKeyDownOnInput}
					role="combobox"
					aria-autocomplete="list"
					aria-expanded={focused && !!results.length}
					aria-controls="autocomplete-results-list"
					aria-activedescendant={
						focusedIndex >= 0 ? `result-${focusedIndex}` : undefined
					}
					autoComplete="off"
				/>
				{search !== '' && (
					<Button
						className={classes(
							styles.crossBtn,
							[styles.hidden, search === ''],
							resetButtonClass,
						)}
						iconUrl={'assets/Cross.svg'}
						onClick={e => {
							e.preventDefault()
							e.stopPropagation()
							setFocused(true)
							setSearch('')
							onChange?.('')
							inputRef.current?.focus()
							// ;(e.target as HTMLButtonElement).parentElement
							// 	?.querySelector('input')
							// 	?.focus()
						}}
						aria-label={translator.translate('input:clear')}
					/>
				)}
				{search === '' && focused && !noGeoloc && (
					<Button
						className={styles.geolocBtn}
						iconUrl={'assets/Geoloc.svg'}
						onClick={() => void handleGeolocalisation()}
						aria-label={translator.translate('input:geoloc')}
					/>
				)}
			</div>
			{focused && !!results.length && (
				<ul
					id="autocomplete-results-list"
					className={classes(styles.results)}
					role="listbox"
					ref={e => {
						if (!e) return
						ulRef.current = e

						if (inputRef) {
							const rect =
								inputRef.current?.getBoundingClientRect()

							if (rect) {
								ulRef.current.style.setProperty(
									'--lc-suggestions-position-left',
									`${rect.left}px`,
								)
								ulRef.current.style.setProperty(
									'--lc-suggestions-width',
									`${rect.width}px`,
								)
								ulRef.current.style.setProperty(
									'--lc-suggestions-marginTop',
									`${rect.height}px`,
								)
							}
						}
					}}
				>
					{results.slice(0, 10).map((result, index) => (
						<li
							className={styles.result}
							key={
								result.type +
								'/' +
								getSuggestion(result).id +
								(result.type === 'address' &&
								result.address.boundary
									? `/${result.address.boundary.insee}`
									: '')
							}
							role="option"
							id={`result-${index}`}
							aria-selected={index === focusedIndex}
							onClick={e => {
								e.preventDefault()
								e.stopPropagation()
								handleResultClick(result)
							}}
							onKeyDown={e => {
								if (e.key === 'Enter') {
									handleResultClick(result)
								}
							}}
						>
							<div
								className={classes(
									styles.typeLabel,
									modeTheme(
										result.type === 'area'
											? 'public-transport'
											: result.type === 'line'
												? 'public-transport'
												: 'all',
									),
								)}
							>
								{renderAutocompleteIcon(result)}
								{!isMobile && isSearchBar && (
									<>
										{result.type === 'line' && (
											<Translation id="autocomplete:prefix-line" />
										)}
										{result.type === 'area' && (
											<Translation id="autocomplete:prefix-area" />
										)}
									</>
								)}
							</div>
							{resultBody(result)}
						</li>
					))}
				</ul>
			)}
		</div>
	)
}

export function NewAutocompleteInput({
	search: initialSearch,
	types,
	localItems,
	poiCategories,
	...props
}: IntrinsicAutocompleteInputProps & {
	search: string
	types: AutocompleteSearch[]
	localItems?: AutocompleteResult[] | Poi[]
	poiCategories?: string[]
}) {
	const { search, setSearch, results } = useNewAutocomplete({
		search: initialSearch,
		types,
		localItems,
		poiCategories,
	})

	return (
		<NewControlledAutocompleteInput
			{...props}
			search={search}
			setSearch={setSearch}
			results={results}
		/>
	)
}

function ModalWrapper<
	P extends {
		ref?: ForwardedRef<HTMLInputElement>
		keepFocus?: boolean
		onFocus?: () => void
	},
>(props: { Component: ComponentType<P> } & P): Children
function ModalWrapper<
	P extends {
		ref?: ForwardedRef<HTMLInputElement>
		keepFocus?: boolean
		onFocus?: () => void
		onBlur?: () => void
	},
>({
	Component,
	ref,
	...props
}: {
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	Component: ComponentType<any>
} & P) {
	const [modal, setModal] = useState(false)
	const mobile = useMobile()
	const refs = useMemo(() => mergeRefs(ref, x => x?.focus()), [ref])

	if (modal && mobile)
		return (
			<Modal
				onClose={() => setModal(false)}
				fullModal
				className={styles.modal}
			>
				<Component
					{...props}
					keepFocus
					onBlur={() => setModal(false)}
					ref={refs}
				/>
			</Modal>
		)

	return <Component {...props} onFocus={() => setModal(true)} ref={ref} />
}

export function NewControlledAutocomplete(
	props: ComponentProps<typeof NewControlledAutocompleteInput>,
) {
	return (
		<ModalWrapper Component={NewControlledAutocompleteInput} {...props} />
	)
}

export function NewAutocomplete(
	props: ComponentProps<typeof NewAutocompleteInput>,
) {
	return <ModalWrapper Component={NewAutocompleteInput} {...props} />
}

// A refaire
function filterResults(
	results: (AutocompleteResult & { def: string })[],
	configSearch: ConfigSearch,
	currentModule: ModuleObject,
): (AutocompleteResult & { def: string })[] {
	const { count, order } = configSearch.results

	const remaining = new Set(results)
	const filteredResults: (AutocompleteResult & { def: string })[][] = []
	let length = 0
	for (const rule of order) {
		void rule
		filteredResults.push([])
	}

	while (length < count && remaining.size && order.length) {
		rule: for (const [key, rule] of order.entries()) {
			if (length >= count) continue

			for (const result of remaining) {
				if (matchSearchRule(result, rule, currentModule)) {
					filteredResults[safeProp(key)].push(result)
					remaining.delete(result)
					length++
					continue rule
				}
			}
		}
	}
	return filteredResults.flat()
}

function resultBody(result: AutocompleteResult & { def: string }) {
	switch (result.type) {
		case 'address':
			return (
				<div className={styles.resultBody}>
					<span className={styles.resultBoundary}>
						{result.address.boundary?.name}
					</span>
					{result.address.name}
				</div>
			)
		case 'area':
			return (
				<div className={styles.resultBody}>
					<span className={styles.resultBoundary}>
						{result.area.boundaries
							?.filter(b => b.level === 8)
							.map(b => b.name)
							.join(', ')}
					</span>
					{result.area.name}
				</div>
			)
		case 'poi':
			return (
				<div className={styles.resultBody}>
					<span className={styles.resultBoundary}>
						{result.poi.boundary?.name}
					</span>
					{result.poi.name}
				</div>
			)
		case 'boundary':
			return result.boundary.name
		case 'line':
			return (
				<div className={styles.resultLines}>
					<LinePicto className={styles.lineIcon} line={result.line} />
					<div className={styles.routeContainer}>
						{result.line.od.map(route => {
							return (
								<div
									className={styles.nameRoute}
									key={route.name}
								>
									<span>{route.name}</span>
									<span className={styles.suggestionBoundary}>
										{`(${route.boundaries.join(', ')})`}
									</span>
								</div>
							)
						})}
					</div>
				</div>
			)
	}
}
